import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Archive,
  Filter,
  CalendarRange,
  Building2,
  Layers,
  Flame,
  RefreshCw,
  Search,
  Folder,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import { apiClient, RegulatoryChange } from '../lib/api';
import { decodeHtmlEntities } from '../utils/text';
import { normalizeDocument, PolicyUpdate, getDocumentTitle } from '../utils/documentUtils';
import ReportDetail from './ReportDetail';

type Urgency = 'High' | 'Medium' | 'Low';

const COLORS = ['#4f46e5', '#0ea5e9', '#22c55e', '#f97316', '#facc15', '#ec4899', '#6366f1', '#8b5cf6'];

const KEY_FILTERS = [
  { id: 'cms', label: 'CMS', type: 'agency', value: 'CMS' },
  { id: 'cdc', label: 'CDC', type: 'agency', value: 'CDC' },
  { id: 'fda', label: 'FDA', type: 'agency', value: 'FDA' },
  { id: 'high', label: 'High Urgency', type: 'urgency', value: 'High' },
  { id: 'policy', label: 'Policy Changes', type: 'category', value: 'Policy Changes' },
  { id: 'clinical', label: 'Clinical Guidelines', type: 'category', value: 'Clinical Guidelines' },
  { id: '30days', label: 'Last 30 Days', type: 'date', value: 30 },
  { id: '90days', label: 'Last 90 Days', type: 'date', value: 90 },
] as const;

function deriveUrgency(doc: RegulatoryChange): Urgency {
  const analysis: any = (doc as any).simplified_analysis || {};
  const priority = (doc as any).urgency_level || (doc as any).priority || analysis.priority;
  if (typeof priority === 'string') {
    const normalized = priority.toLowerCase();
    if (normalized.includes('high') || normalized.includes('urgent')) return 'High';
    if (normalized.includes('low')) return 'Low';
  }
  if (doc.simplified_analysis?.compliance_deadline) {
    const deadline = new Date(doc.simplified_analysis.compliance_deadline);
    if (!Number.isNaN(deadline.getTime())) {
      const diffDays = (deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
      if (diffDays < 30) return 'High';
      if (diffDays < 90) return 'Medium';
    }
  }
  return 'Medium';
}

function getCategory(doc: RegulatoryChange): string {
  const analysis: any = (doc as any).simplified_analysis || {};
  return (
    (doc as any).category ||
    analysis.category ||
    doc.source ||
    'General'
  );
}

function getAgency(doc: RegulatoryChange): string {
  return ((doc as any).agency || doc.source || 'Unknown').trim() || 'Unknown';
}

function getFullTitle(doc: RegulatoryChange): string {
  const part1 = (doc as any).title_part1 || '';
  const part2 = (doc as any).title_part2 || '';
  return (
    (doc as any).full_title ||
    doc.title ||
    `${part1} ${part2}`.trim() ||
    'Untitled Document'
  );
}

function matchesDays(doc: RegulatoryChange, days: number): boolean {
  const dateString = doc.date || (doc as any).published_date || doc.analysis_timestamp || doc.discovered_date;
  if (!dateString) return false;
  const docDate = new Date(dateString);
  if (Number.isNaN(docDate.getTime())) return false;
  const diffDays = (Date.now() - docDate.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays <= days;
}

function getSummary(doc: RegulatoryChange): string {
  return (
    (doc as any).summary ||
    doc.simplified_analysis?.paragraph_summary ||
    doc.simplified_analysis?.one_sentence_summary ||
    doc.simplified_analysis?.detailed_summary ||
    'No summary available'
  );
}

function toPolicyUpdate(doc: RegulatoryChange): PolicyUpdate {
  const normalized = normalizeDocument(doc);
  return {
    ...normalized,
    title: getDocumentTitle(doc),
    summary: getSummary(doc),
    urgency: deriveUrgency(doc),
    affectedDepartments: doc.simplified_analysis?.who_this_affects?.departments || normalized.affectedDepartments || [],
    sourceDocuments: [{
      title: getFullTitle(doc),
      url: doc.document_info?.pdf_url || (doc as any).pdf_url || (doc as any).document_url || (doc as any).url,
      type: doc.source || 'Archive'
    }],
    originalData: doc
  };
}

function getPdfUrl(doc: RegulatoryChange): string | null {
  const pdfUrl = (doc as any).pdf_url || doc.document_info?.pdf_url || (doc as any).document_url;
  if (pdfUrl) return pdfUrl;
  const url = (doc as any).url || (doc as any).s3_url;
  if (url && /\.pdf(\?|$)/i.test(url)) return url;
  return null;
}

export function ArchivePage() {
  const [documents, setDocuments] = useState<RegulatoryChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<RegulatoryChange | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyUpdate | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const loadArchive = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [docs, health] = await Promise.all([
        apiClient.getArchivedChanges(500),
        apiClient.getArchiveHealth().catch(() => null),
      ]);
      setDocuments(docs);
      setTotalCount(health?.total_documents ?? docs.length);
    } catch (err: any) {
      console.error('Failed to load archive', err);
      setError('Unable to load archived documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArchive();
  }, [loadArchive]);

  const toggleKeyFilter = (id: string) => {
    setActiveFilters((prev) => prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]);
  };

  const clearActiveFilters = () => setActiveFilters([]);

  const derivedCounts = useMemo(() => {
    const agencyCounts: Record<string, number> = {};
    const categoryCounts: Record<string, number> = {};
    const deptCounts: Record<string, number> = {};
    const urgencyCounts: Record<string, number> = { High: 0, Medium: 0, Low: 0 };
    documents.forEach((doc) => {
      const agency = getAgency(doc);
      agencyCounts[agency] = (agencyCounts[agency] || 0) + 1;

      const category = getCategory(doc);
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;

      const departments = doc.simplified_analysis?.who_this_affects?.departments || [];
      departments.forEach((dept) => {
        deptCounts[dept] = (deptCounts[dept] || 0) + 1;
      });

      const urgency = deriveUrgency(doc);
      urgencyCounts[urgency] += 1;
    });
    return { agencyCounts, categoryCounts, deptCounts, urgencyCounts };
  }, [documents]);

  const keyFiltersById = useMemo(() => {
    const map = new Map<string, typeof KEY_FILTERS[number]>();
    KEY_FILTERS.forEach((f) => map.set(f.id, f));
    return map;
  }, []);

  const docMatchesFilter = (doc: RegulatoryChange, filterId: string): boolean => {
    const filter = keyFiltersById.get(filterId);
    if (!filter) return true;
    const agency = getAgency(doc);
    const category = getCategory(doc);
    const urgency = deriveUrgency(doc);
    switch (filter.type) {
      case 'agency':
        return agency.toLowerCase() === String(filter.value).toLowerCase();
      case 'category':
        return category.toLowerCase() === String(filter.value).toLowerCase();
      case 'urgency':
        return urgency === filter.value;
      case 'date':
        return matchesDays(doc, filter.value as number);
      default:
        return true;
    }
  };

  const filteredDocuments = useMemo(() => {
    const searchLower = search.toLowerCase();
    return documents.filter((doc) => {
      if (activeFilters.length > 0) {
        const allMatch = activeFilters.every((id) => docMatchesFilter(doc, id));
        if (!allMatch) return false;
      } else {
        return false;
      }

      if (searchLower) {
        const title = getDocumentTitle(doc).toLowerCase();
        const summary = getSummary(doc).toLowerCase();
        if (!title.includes(searchLower) && !summary.includes(searchLower)) return false;
      }
      return true;
    });
  }, [documents, activeFilters, search, keyFiltersById]);

  const getFilterCount = (filterId: string) => {
    return documents.filter((doc) => docMatchesFilter(doc, filterId)).length;
  };

  const volumeByMonth = useMemo(() => {
    const counts: Record<string, number> = {};
    documents.forEach((doc) => {
      const dateString = doc.date || (doc as any).published_date || doc.discovered_date;
      const date = dateString ? new Date(dateString) : new Date();
      if (!Number.isNaN(date.getTime())) {
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .sort(([a], [b]) => (a > b ? 1 : -1))
      .map(([month, count]) => ({ month, count }));
  }, [documents]);

  const agencyChartData = useMemo(
    () => Object.entries(derivedCounts.agencyCounts).map(([name, value]) => ({ name, value })),
    [derivedCounts.agencyCounts]
  );
  const categoryChartData = useMemo(
    () => Object.entries(derivedCounts.categoryCounts).map(([name, value]) => ({ name, value })),
    [derivedCounts.categoryCounts]
  );

  const activeFilterBadges = useMemo(() => {
    return activeFilters.map((id) => {
      const filter = keyFiltersById.get(id);
      return filter ? filter.label : id;
    });
  }, [activeFilters, keyFiltersById]);

  const renderDocumentCard = (doc: RegulatoryChange) => {
    const agency = getAgency(doc);
    const category = getCategory(doc);
    const urgency = deriveUrgency(doc);
    const summary = getSummary(doc);
    const title = getDocumentTitle(doc);
    const date = doc.date || (doc as any).published_date || doc.discovered_date || 'N/A';

    return (
      <Card
        key={`${doc.document_id || doc.title}`}
        className="p-4 border border-border shadow-sm hover:shadow-lg transition-shadow cursor-pointer"
        onClick={() => {
          setSelectedDoc(doc);
          setSelectedPolicy(toPolicyUpdate(doc));
          setShowDetail(true);
        }}
      >
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{agency}</Badge>
            <Badge variant="outline">{category}</Badge>
            <Badge variant={urgency === 'High' ? 'destructive' : urgency === 'Medium' ? 'secondary' : 'outline'}>
              {urgency} urgency
            </Badge>
            <Badge variant="outline">{date}</Badge>
          </div>
          <h3 className="text-lg font-semibold leading-tight" title={decodeHtmlEntities(doc.title || '')}>
            {title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-3">
            {summary || 'No summary available'}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {(doc.simplified_analysis?.who_this_affects?.departments || []).slice(0, 4).map((dept) => (
              <Badge key={dept} variant="outline">
                {dept}
              </Badge>
            ))}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {showDetail && selectedDoc && selectedPolicy && (
        <ReportDetail
          document={selectedPolicy}
          onBack={() => {
            setShowDetail(false);
            setSelectedDoc(null);
            setSelectedPolicy(null);
          }}
          source="archive"
          prefetchedDocument={selectedDoc}
        />
      )}
      {!showDetail && (
      <div className="p-6 space-y-5 overflow-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Archive className="w-6 h-6 text-primary" />
              <h1 className="text-2xl font-bold">Archived Regulatory Documents</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {loading ? 'Loading archive…' : `${totalCount ?? documents.length} total`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
              <input
                className="pl-9 pr-3 py-2 border rounded-md bg-card text-sm"
                placeholder="Search titles or summaries"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button variant="outline" onClick={loadArchive}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-primary" />
            <h3 className="font-semibold">Quick Filters</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {KEY_FILTERS.map((filter) => {
              const isActive = activeFilters.includes(filter.id);
              const count = getFilterCount(filter.id);
              return (
                <Button
                  key={filter.id}
                  variant={isActive ? 'default' : 'outline'}
                  className="h-10"
                  onClick={() => toggleKeyFilter(filter.id)}
                >
                  {filter.label}
                  <Badge variant="secondary" className="ml-2">{count}</Badge>
                </Button>
              );
            })}
            {activeFilters.length > 0 && (
              <Button variant="ghost" onClick={clearActiveFilters}>
                Clear filters
              </Button>
            )}
          </div>
        </Card>

        {activeFilterBadges.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {activeFilterBadges.map((badge) => (
              <Badge key={badge} variant="secondary">
                {badge}
              </Badge>
            ))}
          </div>
        )}

        {activeFilters.length === 0 && (
          <div className="space-y-4">
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-4 h-4 text-primary" />
                <h3 className="font-semibold">Archive Overview</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-accent/30">
                  <div className="text-xs uppercase text-muted-foreground">Total Docs</div>
                  <div className="text-xl font-semibold">{totalCount ?? documents.length}</div>
                </div>
                <div className="p-3 rounded-lg bg-accent/30">
                  <div className="text-xs uppercase text-muted-foreground">Last 30 days</div>
                  <div className="text-xl font-semibold">
                    {documents.filter((d) => matchesDays(d, 30)).length}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-accent/30">
                  <div className="text-xs uppercase text-muted-foreground">High urgency</div>
                  <div className="text-xl font-semibold">{derivedCounts.urgencyCounts.High}</div>
                </div>
                <div className="p-3 rounded-lg bg-accent/30">
                  <div className="text-xs uppercase text-muted-foreground">Agencies</div>
                  <div className="text-xl font-semibold">{Object.keys(derivedCounts.agencyCounts).length}</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-3">Select a filter above to view documents.</p>
            </Card>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <Card className="p-4 col-span-1 xl:col-span-2">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarRange className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold">Volume Over Time</h3>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={volumeByMonth}>
                      <XAxis dataKey="month" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#4f46e5" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold">By Agency</h3>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={agencyChartData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70}>
                        {agencyChartData.map((_, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>

            <Card className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Layers className="w-4 h-4 text-primary" />
                <h3 className="font-semibold">Top Categories</h3>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryChartData.slice(0, 10)}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#22c55e" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        )}

        {activeFilters.length > 0 && (
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Folder className="w-4 h-4 text-primary" />
              <h3 className="font-semibold">Documents ({filteredDocuments.length})</h3>
            </div>
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading archived documents…</div>
            ) : error ? (
              <div className="text-sm text-destructive">{error}</div>
            ) : filteredDocuments.length === 0 ? (
              <div className="text-sm text-muted-foreground">No documents match these filters.</div>
            ) : (
              <div className="space-y-3">
                {filteredDocuments.map((doc) => renderDocumentCard(doc))}
              </div>
            )}
          </Card>
        )}
      </div>
      )}
    </div>
  );
}

export default ArchivePage;
